import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CURRENCIES, currencyMeta, formatHuman, formatRateHuman, parseRate, toBase, type Money } from '@fint/money';
import { api } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { formatDate } from '@/lib/dates';
import { Input, SelectField } from '@/features/common/ui';

/**
 * The currency picker and the rate field, shared by the invoice composer,
 * the payment drawer and the customer form.
 *
 * ── THE RULE THE FIELDS FOLLOW ──
 * A document is owed in its own currency; a book is kept in one. A document
 * in another currency needs a rate - base units per ONE unit, rupees per
 * pound - fixed when it is saved. The rate is PREFILLED from the book's
 * rate table and the published feeds (`GET /rates`) and always typeable
 * over; what is sent is the number on screen, with where it came from.
 * A feed that is down degrades to "type the rate", never to a save that
 * quietly used a rate nobody saw.
 */

export interface RateQuote {
  rate: number | null;
  date: string;
  source: 'fbil' | 'ecb' | 'manual' | null;
  from: string;
  to: string | null;
}

/** The published rate for a day, or `rate: null`. Cached a minute: the same day is asked about on every keystroke. */
export function useRateQuote(date: string | undefined, from: string | undefined, to: string | undefined, enabled = true) {
  const org = useActiveOrg();
  const foreign = !!from && !!to && from !== to;
  return useQuery({
    queryKey: ['rate', org?.id, date, from, to],
    queryFn: () => api.get<RateQuote>(`/rates?date=${date}&from=${from}&to=${to}`),
    enabled: !!org?.id && enabled && foreign && !!date && /^\d{4}-\d{2}-\d{2}$/.test(date),
    staleTime: 60_000,
  });
}

export type RateSource = 'manual' | 'fbil' | 'ecb' | 'last' | 'inherited' | 'bank';

/**
 * A rate that follows the day and the currency until a person types one.
 *
 * `initial` is a stored rate (an edited draft) and wins until the day or the
 * currency changes; a prefill never overwrites a typed rate; clearing the
 * field lets the prefill back in.
 */
export function useCurrencyRate(options: { date: string; currency: string; base: string; initial?: { rate: string | number; source: string | null } | null }) {
  const foreign = options.currency !== options.base;
  const [rate, setRate] = useState('');
  const [source, setSource] = useState<RateSource>('manual');
  // Whether the figure on screen is the person's own. A rate saved in
  // Settings is also 'manual' on the wire, but it was prefilled, not typed.
  const [typed, setTyped] = useState(false);
  const [seeded, setSeeded] = useState<string | null>(null);
  const quote = useRateQuote(options.date, options.currency, options.base, foreign && !typed);

  // The stored rate, once, for the document it belongs to.
  useEffect(() => {
    if (!options.initial || seeded === `${options.initial.rate}`) return;
    setSeeded(`${options.initial.rate}`);
    setRate(formatRateHuman(options.initial.rate));
    setSource((options.initial.source as RateSource) ?? 'manual');
    // A stored rate is kept as it is until the person changes it.
    setTyped(true);
  }, [options.initial, seeded]);

  // The prefill: only into a field nobody has typed in.
  useEffect(() => {
    if (!foreign || typed) return;
    const q = quote.data;
    if (q?.rate) {
      setRate(formatRateHuman(q.rate));
      setSource(q.source ?? 'manual');
    }
  }, [quote.data, foreign, typed]);

  const parsed = parseRate(rate);
  return {
    foreign,
    rate,
    /** Typing makes it the person's; a blank lets the prefill back in. */
    setRate: (next: string) => {
      setRate(next);
      setSource('manual');
      setTyped(next.trim() !== '');
    },
    source,
    typed,
    parsed,
    quote,
    /** Ready to save: in the base, or a rate on screen. */
    ready: !foreign || parsed !== undefined,
    /** The base value of a document-currency amount at the rate on screen. */
    inBase: (m: Money): Money | null => (foreign ? (parsed ? toBase(m, options.base, parsed) : null) : m),
  };
}

export function CurrencySelect({
  id,
  label = 'Currency',
  value,
  onChange,
  base,
  hint,
  disabled,
}: {
  id: string;
  label?: string;
  value: string;
  onChange: (code: string) => void;
  base: string;
  hint?: string;
  disabled?: boolean;
}) {
  const ordered = [...CURRENCIES].sort((a, b) => (a.code === base ? -1 : b.code === base ? 1 : a.name.localeCompare(b.name)));
  return (
    <div>
      <SelectField id={id} label={label} value={value} onChange={onChange}>
        {ordered.map((c) => (
          <option key={c.code} value={c.code} disabled={disabled}>
            {c.code} - {c.name}
            {c.code === base ? ' (the book’s currency)' : ''}
          </option>
        ))}
      </SelectField>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/** Where a prefilled rate came from, as a sentence under the field. */
export function rateHint(currency: string, base: string, source: RateSource, quote: RateQuote | undefined, loading: boolean, typed: boolean): string {
  if (typed) return `${base} per ${currency}. Typed by you; the published rate is a prefill, never a rule.`;
  if (source === 'bank') return `${base} per ${currency}, derived from the two amounts.`;
  if (loading) return 'Looking up the published rate…';
  if (quote?.rate && quote.source === 'fbil') return `FBIL reference rate for ${formatDate(quote.date)}.`;
  if (quote?.rate && quote.source === 'ecb') return `ECB reference rate for ${formatDate(quote.date)} - FBIL had none for that day.`;
  if (quote?.rate && quote.source === 'manual') return `The rate saved in Settings for ${formatDate(quote.date)}.`;
  return `No published rate found for that day - type the rate, ${base} per ${currency}.`;
}

export function RateField({
  id,
  currency,
  base,
  rate,
  onChange,
  source,
  quote,
  loading,
  typed = false,
  error,
  preview,
  testId,
}: {
  id?: string;
  currency: string;
  base: string;
  rate: string;
  onChange: (next: string) => void;
  source: RateSource;
  quote?: RateQuote;
  loading: boolean;
  /** The figure is the person's own, not a prefill. */
  typed?: boolean;
  error?: string;
  /** A document-currency figure to show in the base beside the rate. */
  preview?: Money | null;
  testId?: string;
}) {
  const parsed = parseRate(rate);
  const inBase = preview && parsed ? toBase(preview, base, parsed) : null;
  return (
    <Input
      id={id}
      label={`Exchange rate: 1 ${currency} = ${currencyMeta(base).symbol}`}
      inputMode="decimal"
      value={rate}
      onChange={(e) => onChange(e.target.value)}
      placeholder="e.g. 127.5"
      error={error}
      hint={`${rateHint(currency, base, source, quote, loading, typed)}${inBase && preview ? ` ${formatHuman(preview)} ≈ ${formatHuman(inBase)}.` : ''}`}
      data-testid={testId ?? 'exchange-rate'}
    />
  );
}
